import { CurrencyPipe, NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { Params, Router, RouterLink } from '@angular/router';
import {
	BreakPointService,
	FeatureService,
	SrPunctuationPipe,
	SvgIconComponent,
	TruncatedCurrencyPipe,
} from '@woolworthsnz/styleguide';
import { ContextResponse, PriceResponse, ProductTag } from '@woolworthsnz/trader-api';
import { getOfferText, shouldShowTagAsStrap } from '../../helpers/';
import { mapRewardTypeToBoostStrap } from '@woolworthsnz/everyday-rewards';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const tagTypeEnum = ProductTag.TagTypeEnum;

export const productTagBackgroundImages = {
	[tagTypeEnum.HasBonusPoints]: 'badge-points.png',
	[tagTypeEnum.IsClubPrice]: 'badge-onecard.png',
	[tagTypeEnum.IsSpecial]: 'badge-special.png',
	[tagTypeEnum.IsMultiBuy]: 'badge-multi-buy.png',
	[tagTypeEnum.IsOnlineOnly]: 'badge-online-only.png',
	[tagTypeEnum.IsFreshDeal]: 'badge-multi-buy.png',
	[tagTypeEnum.IsGreatPrice]: 'badge-great-price.png',
	[tagTypeEnum.IsGreatPriceMultiBuy]: 'badge-great-price-multi-buy.png',
	[tagTypeEnum.IsTargetedOffer]: 'badge-targeted-offer.png',
	[`${tagTypeEnum.IsTargetedOffer}-uncropped`]: 'badge-targeted-offer-uncropped.png',
	[tagTypeEnum.Other]: undefined,
};

@Component({
	selector: 'product-tag, [productTag]',
	template: `
		<ng-template #tagTemplate>
			<svg
				id="product-{{ uniqueId }}-promoInfo"
				attr.viewbox="0 0 {{ size }} {{ size }}"
				attr.width="{{ size }}"
				attr.height="{{ size }}"
				[attr.aria-label]="tagAriaLabel | srPunctuation"
			>
				<image
					*ngIf="tagImage"
					[attr.href]="tagImage"
					[attr.xlink:href]="tagImage"
					attr.height="{{ size }}"
					attr.width="{{ size }}"
					data-cy="tagImage"
				/>

				<ng-container *ngIf="tag?.bonusPoints; let bonus">
					<text x="50%" y="30" text-anchor="middle" fill="white">
						{{ bonus.quantity }}
					</text>
				</ng-container>
				<ng-container *ngIf="tag?.multiBuy; let multiBuy">
					<text [class.fixed]="fixedSize" class="{{ classNameTagType }}-quantity" text-anchor="middle">
						{{ multiBuy.quantity }} for
					</text>
					<text [class.fixed]="fixedSize" class="{{ classNameTagType }}-value" text-anchor="middle">
						<tspan dy="2" class="multiBuy-value">{{ multiBuy.value | truncatedCurrency }}</tspan>
					</text>
				</ng-container>
			</svg>
			<span *ngIf="targetedOfferText">{{ targetedOfferText }}</span>
		</ng-template>

		<ng-template #strapTemplate>
			<div
				id="product-{{ uniqueId }}-promoInfo"
				class="productStrap {{ classNameTagType }}"
				[ngClass]="{
					onecard: !isHaystackEDRChangesEnabled,
					'new-low-price-enabled': isNewLowPriceEnabled && (isGreatPrice || isGreatPriceMultiBuy),
				}"
			>
				<div class="productStrap-title" [attr.aria-label]="strapTitle | srPunctuation">
					<cdx-svg-icon name="onecard-active" *ngIf="showClubPriceIcon"></cdx-svg-icon>
					<cdx-svg-icon name="roundel-only" *ngIf="this.isTargetedOffer"></cdx-svg-icon>
					<cdx-svg-icon name="rocket" *ngIf="this.isBoostOffer" size="default"></cdx-svg-icon>
					{{ strapTitle }}
				</div>
				<cdx-svg-icon name="fun-green-arc" class="arcIcon" *ngIf="showFunGreenArc"></cdx-svg-icon>
				<cdx-svg-icon name="light-orange-arc" class="arcIcon" *ngIf="showLightOrangeArc"></cdx-svg-icon>
				<div class="productStrap-text" [attr.aria-label]="strapText | srPunctuation">{{ strapText }}</div>
			</div>
		</ng-template>

		<a
			[attr.aria-labelledby]="ariaLabelledBy"
			[routerLink]="link?.path"
			[queryParams]="link?.queryParams"
			*ngIf="tagLink; else unlinked; let link"
			[target]="tag.additionalTag?.linkTarget"
			[attr.aria-hidden]="carouselindex !== undefined ? carouselindex !== 0 : null"
			tabindex="-1"
		>
			<ng-container *ngIf="displayStrap; then strapTemplate; else tagTemplate"></ng-container>
		</a>

		<ng-template #unlinked>
			<ng-container *ngIf="displayStrap; then strapTemplate; else tagTemplate"></ng-container>
		</ng-template>
	`,
	styleUrls: ['tag.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, SvgIconComponent, RouterLink, SrPunctuationPipe, TruncatedCurrencyPipe, NgClass],
})
export class TagComponent implements OnInit {
	@Input() tag: ProductTag;
	@Input() price: PriceResponse;
	@Input() uniqueId: number;

	@HostBinding('attr.data-fixed-size')
	@Input()
	fixedSize = false;

	@Input()
	targetedOfferCropped = true;

	@Input()
	showTargetedOfferText = true;

	@Input()
	asStrap = false;

	@Input() carouselindex: number;

	@HostBinding('attr.data-targeted-offer')
	get isCroppedTargetedOffer(): boolean {
		return this.tag?.tagType === tagTypeEnum.IsTargetedOffer && this.targetedOfferCropped;
	}

	private _size: number;

	@HostBinding('attr.data-product-strap')
	get displayStrap(): boolean {
		return this.areProductStrapsEnabled && this.asStrap && shouldShowTagAsStrap(this.tag?.tagType);
	}

	@HostBinding('class.haystack-edr-changes-enabled')
	get isHaystackEDRChangesEnabled(): boolean {
		return this.haystackEDRChangesEnabled;
	}

	@HostBinding('class.new-low-price-enabled')
	get isNewLowPriceEnabled(): boolean {
		return this.newProductTagLowPriceEnabled;
	}

	get isGreatPrice(): boolean {
		return this.tag?.tagType === tagTypeEnum.IsGreatPrice;
	}

	get isGreatPriceMultiBuy(): boolean {
		return this.tag?.tagType === tagTypeEnum.IsGreatPriceMultiBuy;
	}

	get strapTitle(): string | undefined {
		switch (this.tag?.tagType) {
			case tagTypeEnum.IsClubPrice: {
				return this.haystackEDRChangesEnabled ? 'Member Price' : 'Club Price';
			}
			case tagTypeEnum.IsSpecial:
			case tagTypeEnum.IsMultiBuy: {
				return 'Special';
			}
			case tagTypeEnum.IsFreshDeal: {
				return 'Fresh Deal';
			}

			case tagTypeEnum.IsGreatPrice:
			case tagTypeEnum.IsGreatPriceMultiBuy: {
				if (this.newProductTagLowPriceEnabled) {
					return 'LOW PRICE';
				} else if (this.productTagLowPriceEnabled) {
					return 'Low Price';
				} else {
					return 'Great Price';
				}
			}

			case tagTypeEnum.IsSampled: {
				return 'Sampled';
			}
			case tagTypeEnum.IsFreeSample: {
				return 'Free Sample';
			}
			case tagTypeEnum.IsOnlineOnly: {
				return 'Online Only';
			}
		}
	}

	get strapText(): string | undefined {
		switch (this.tag?.tagType) {
			case tagTypeEnum.IsSpecial:
				return this.specialStrapText();
			case tagTypeEnum.IsMultiBuy:
			case tagTypeEnum.IsGreatPriceMultiBuy:
			case tagTypeEnum.IsOnlineOnly:
			case tagTypeEnum.IsFreshDeal: {
				return `${this.tag.multiBuy?.quantity} for ${this.currencyPipe.transform(this.tag.multiBuy?.value)}`;
			}
			case tagTypeEnum.IsTargetedOffer: {
				return this.targetedOfferText;
			}
			case tagTypeEnum.IsBoostOffer: {
				return this.tag?.boostOffer?.rewardValue
					? mapRewardTypeToBoostStrap(this.tag.boostOffer.rewardValue, undefined)
					: '';
			}
		}
	}

	get classNameTagType(): string {
		let tagTypeString = this.tag?.tagType ? this.tag?.tagType.toString() : '';
		if (this.haystackEDRChangesEnabled && tagTypeString === 'IsClubPrice') {
			tagTypeString = 'IsMemberPrice';
		}
		const className = tagTypeString.charAt(0).toLowerCase() + tagTypeString.substring(1);
		return this.price?.savePercentage === 51 &&
			this.showDiscountPercentageOnStrap &&
			this.tag?.tagType === tagTypeEnum.IsSpecial
			? 'isSpecial--extend'
			: className;
	}

	get isTargetedOffer(): boolean {
		return this.tag?.tagType === tagTypeEnum.IsTargetedOffer;
	}

	get isBoostOffer(): boolean {
		return this.tag?.tagType === tagTypeEnum.IsBoostOffer;
	}

	get isOnlineOnly(): boolean {
		return this.tag?.tagType === tagTypeEnum.IsOnlineOnly;
	}

	get ariaLabelledBy(): string {
		return `product-${this.uniqueId}-promoInfo`;
	}

	get tagAriaLabel(): string {
		if (this.tag?.tagType !== tagTypeEnum.Other) {
			return `Promotion type - ${this.tag?.tagType ?? 'Other'}`;
		}

		return this.tag.additionalTag?.altText ?? 'Promotion type - Other';
	}

	get showClubPriceIcon(): boolean {
		return !this.haystackEDRChangesEnabled && this.tag?.tagType === 'IsClubPrice';
	}

	get showFunGreenArc(): boolean {
		const isGreatPriceTypes = this.isGreatPrice || this.isGreatPriceMultiBuy;

		return (
			!this.isTargetedOffer &&
			!this.isBoostOffer &&
			!(this.haystackEDRChangesEnabled && this.tag?.tagType === tagTypeEnum.IsClubPrice) &&
			!(this.isNewLowPriceEnabled && isGreatPriceTypes)
		);
	}

	get showLightOrangeArc(): boolean {
		return this.isBoostOffer || (this.haystackEDRChangesEnabled && this.tag?.tagType === tagTypeEnum.IsClubPrice);
	}

	private areProductStrapsEnabled: boolean;
	private productTagLowPriceEnabled: boolean;
	private newProductTagLowPriceEnabled: boolean;
	private showDiscountPercentageOnStrap: boolean;
	private displayNewLozenge: boolean;
	public deployUrl = '/assets/images/product-details/tags';
	private haystackEDRChangesEnabled: boolean;
	isSmallDevice$ = this.breakpointService.isSmallDevice$.pipe(takeUntilDestroyed());
	featureState$ = this.featureService.state$.pipe(takeUntilDestroyed());

	constructor(
		private breakpointService: BreakPointService,
		private featureService: FeatureService,
		private cdr: ChangeDetectorRef,
		private router: Router,
		private currencyPipe: CurrencyPipe
	) {}

	get hasBonusPoints(): boolean {
		return this.tag && this.tag.tagType === tagTypeEnum.HasBonusPoints;
	}

	get size(): any {
		return this._size;
	}

	set size(s: number) {
		this._size = s;
	}

	get tagImage(): string | undefined | null {
		if (!this.tag || !this.tag.tagType) {
			return;
		}
		let tagType: string = this.tag.tagType;
		if (this.isTargetedOffer && !this.targetedOfferCropped) {
			tagType = `${this.tag.tagType}-uncropped`;
		}

		if (
			this.tag.tagType === ProductTag.TagTypeEnum.IsSpecial &&
			this.showDiscountPercentageOnStrap &&
			(this.price?.canShowSavings === undefined ? true : this.price.canShowSavings)
		) {
			return this.specialTagImage();
		}

		return (
			(productTagBackgroundImages[this.tag.tagType] &&
				`${this.deployUrl}/${productTagBackgroundImages[tagType]}`) ||
			(this.tag.additionalTag && this.tag.additionalTag.imagePath) ||
			null
		);
	}

	get targetedOfferText(): string | undefined {
		if (!this.isTargetedOffer || !this.showTargetedOfferText) {
			return;
		}
		return getOfferText(this.tag?.targetedOffer);
	}

	get tagLink(): { path: string; queryParams: string | Params } | undefined | null {
		if (!this.tag) {
			return;
		}
		const hasAdditionalTagLink = this.tag.additionalTag && this.tag.additionalTag.link;

		const hasMultiBuyTagLink = this.tag.multiBuy && this.tag.multiBuy.link;

		const path = (link: string): string => link.split('?')[0];
		const params = (link: string): Params => this.router.parseUrl(link).queryParams;

		if (hasMultiBuyTagLink) {
			return {
				path: path(this.tag.multiBuy?.link || ''),
				queryParams: params(this.tag.multiBuy?.link || ''),
			};
		} else if (hasAdditionalTagLink) {
			return {
				path: path(this.tag.additionalTag?.link || ''),
				queryParams: params(this.tag.additionalTag?.link || ''),
			};
		}

		return null;
	}

	ngOnInit(): void {
		this.isSmallDevice$.subscribe((d) => this.setSize(d));

		this.featureState$.subscribe(() => {
			this.areProductStrapsEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.ProductStraps
			);
			this.productTagLowPriceEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.GreatToLowPrice
			);

			this.showDiscountPercentageOnStrap = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.ShowDiscountPercentage
			);

			this.haystackEDRChangesEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.HaystackEDRChanges
			);

			this.newProductTagLowPriceEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.LowPriceProductStrap
			);

			this.displayNewLozenge = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.DisplayNewLozenge
			);

			if (this.productTagLowPriceEnabled) {
				if (this.newProductTagLowPriceEnabled) {
					productTagBackgroundImages[tagTypeEnum.IsGreatPrice] = 'badge-low-price-new.png';
				} else {
					productTagBackgroundImages[tagTypeEnum.IsGreatPrice] = 'badge-low-price.png';
				}
			} else {
				productTagBackgroundImages[tagTypeEnum.IsGreatPrice] = 'badge-great-price.png';
			}

			if (this.haystackEDRChangesEnabled) {
				productTagBackgroundImages[tagTypeEnum.IsClubPrice] = 'badge-member-price.png';
			} else {
				productTagBackgroundImages[tagTypeEnum.IsClubPrice] = 'badge-onecard.png';
			}

			if (this.displayNewLozenge) {
				productTagBackgroundImages[tagTypeEnum.IsNew] = 'badge-new.png';
			}

			this.cdr.markForCheck();
		});
	}

	setSize(small: boolean): void {
		const prevSize = this.size;
		const desktopSize = this.isTargetedOffer ? 53 : 60;
		const mobileSize = this.isOnlineOnly ? 60 : 45;

		this.size = this.fixedSize || small ? mobileSize : desktopSize;

		if (this.size !== prevSize) {
			this.cdr.markForCheck();
		}
	}

	specialTagImage(): string {
		const savePercentage = this.price?.savePercentage;
		switch (savePercentage) {
			case 51:
				return `${this.deployUrl}/special/betterThanHalfPrice.png`;
			case 50:
				return `${this.deployUrl}/special/halfPrice.png`;
			case 20:
			case 25:
			case 30:
			case 33:
			case 40:
				return `${this.deployUrl}/special/${savePercentage}.png`;
			default:
				return `${this.deployUrl}/special/default.png`;
		}
	}

	specialStrapText(): string | undefined {
		if (!this.price?.canShowSavings) {
			return undefined;
		}

		if (
			!this.showDiscountPercentageOnStrap ||
			(this.showDiscountPercentageOnStrap && !this.price?.savePercentage)
		) {
			return this.price?.savePrice ? `Save ${this.currencyPipe.transform(this.price.savePrice)}` : undefined;
		}

		if (this.price?.savePercentage === 51) {
			return 'Better than 1/2 price';
		}

		return this.price?.savePercentage === 50 ? '1/2 Price' : `${this.price.savePercentage}% Off`;
	}
}
